/**
 * File Service Types
 *
 * Types for matter file management including uploads, downloads, and metadata
 */

import { z } from 'zod';
import { acceptedMimeTypesFor } from '@/lib/constants/file-type-accept';

// Core File Types

/**
 * File status as defined by backend
 */
export type FileStatus = 'uploaded' | 'processing' | 'available' | 'failed';

/**
 * File types supported by the system
 */
export type FileType =
  | 'death-certificate'
  | 'will'
  | 'codicil'
  | 'grant-of-representation'
  | 'primary-id'
  | 'secondary-id'
  | 'correspondence-attachment'
  | 'institution-form'
  | 'institution-form-template'
  | 'institution-form-signed'
  | 'letterhead'
  | 'handwritten-signature'
  | 'email-signature'
  | 'additional-document'
  | 'distribution-declaration'
  | 'matter-document'
  | 'unknown-pending-ai-analysis'; // AI processing file type

/**
 * The three matter-level core document slots a file can be marked into
 * (`POST /matters/:matterId/files/:fileId/categorise`, VALE-806).
 *
 * Deliberately narrower than `FileType`: this is the set the endpoint accepts,
 * and widening it here would only move the 400 from the type checker to runtime.
 * Codicils are excluded on purpose — a matter may hold any number of them, so
 * they append their own slot rather than competing for one (see
 * `documents-bucketing.ts`).
 */
export type CoreSlotFileType = Extract<
  FileType,
  'will' | 'death-certificate' | 'grant-of-representation'
>;

/**
 * The two contact-level identity document slots
 * (`POST /matters/:matterId/files/:fileId/assign-identity`, VALE-806).
 */
export type IdentitySlotFileType = Extract<
  FileType,
  'primary-id' | 'secondary-id'
>;

/**
 * FileType object from backend
 */
export interface FileTypeInfo {
  id: number;
  name: string;
  slug: string;
  category: string;
}

/**
 * Why a document's contents could not be read by AI analysis. Mirrors the backend's
 * `AnalysisFailureReason` members that reach the wire — the transient and catch-all
 * members never do, because a retryable provider outage is not an unreadable file.
 */
export type AnalysisFailureReasonCode =
  | 'DOCUMENT_PASSWORD_PROTECTED'
  | 'DOCUMENT_UNREADABLE';

/**
 * Present only on a document whose contents were never read. Its presence IS the
 * flag — the backend omits the whole object otherwise — so there is no way to render
 * the "couldn't be read" state without the reason that picks the copy.
 */
export interface FileAnalysisFailure {
  reasonCode: AnalysisFailureReasonCode;
}

/**
 * File entity from backend
 */
export interface MatterFile {
  id: number;
  originalFilename: string;
  displayName?: string;
  status: FileStatus;
  s3Key: string;
  imageS3Key?: string;
  mimeType: string;
  fileSizeKb?: number; // Backend returns KB, not bytes
  sizeBytes?: number; // Calculated field for compatibility
  fileType: string | FileTypeInfo; // Can be either string (for compatibility) or object from backend
  extractedData?: Record<string, unknown>;
  isCertifiedCopy: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  metadata?: Record<string, unknown>;
  analysisMetadata?: Record<string, unknown>;
  imageMimeType?: string | null;
  hasThumbnail?: boolean;
  hasImage?: boolean;
  /** Set only when AI analysis could not read this document's contents. */
  analysisFailure?: FileAnalysisFailure | null;
  /**
   * One line saying what this document is — the user's own note if someone has
   * written one, otherwise the sentence the document analysis produced.
   *
   * A single field rather than two, because the row shows one line and the
   * backend has already resolved the precedence. Null when the document has not
   * been analysed and nobody has written a note.
   */
  notes?: string | null;
  /**
   * Who wrote `notes` — the user, or the document analysis.
   *
   * **Nothing renders this today**, and that is deliberate rather than an
   * oversight: the drawer showed it as a "From Vale" badge and the badge was cut
   * for not being useful. It stays on the API type because it is the only thing
   * that distinguishes two states `notes` alone cannot — a user note that
   * happens to read identically to the analysis's sentence — and because it is
   * what any future revert affordance would key off. Do not infer from its
   * presence that a surface displays provenance.
   */
  notesSource?: 'user' | 'ai' | null;

  // Relations
  matterId?: number;
  uploadedBy?: {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
  };
}

/**
 * File summary for list views
 */
export interface FileSummary {
  id: number;
  displayName: string;
  originalFilename: string;
  fileType: string;
  mimeType: string;
  sizeBytes: number;
  status: FileStatus;
  uploadedAt: string;
  uploadedBy?: string;
}

// Request/Response Types

/**
 * Get presigned URL request
 * For institution forms, use metadata to pass institutionId, institutionFormId, and signatoryContactId
 */
export interface GetPresignedUrlRequest {
  fileType: FileType;
  matterId?: number;
  isCertifiedCopy?: boolean;
  lawFirmId?: number;
  lawFirmUserId?: number;
  contactId?: number;
  attachmentId?: number; // For correspondence attachments
  intakeFormId?: string; // For linking uploads to an intake form
  // Opt a typed-fileType upload into the AI consolidation pipeline (LF-3489/LF-2797).
  // When true, the file is enrolled in the same per-document analysis + matter consolidation
  // flow as `fileType: 'unknown-pending-ai-analysis'` while keeping its original type.
  analysisRequested?: boolean;
  // Opt an additional-document upload into type reclassification (LF-4019). When the matter is still
  // missing a primary document (will / death-certificate), the backend analyses the file and, if it
  // classifies as one of those at HIGH confidence, auto-assigns it to the matter and updates its type.
  reclassify?: boolean;
  metadata?: Record<string, unknown>;
}

/**
 * Presigned URL response
 */
export interface PresignedUrlResponse {
  presignedUrl: string;
  fileId: number;
}

/**
 * Upload complete request
 * Backend requires fileId in URL path and originalFilename in body
 */
export interface UploadCompleteRequest {
  fileId: number;
  /**
   * The name of the file the user picked, extension included (`scan_0142.pdf`).
   * Never a human-facing label — that is `displayName`. Putting a label here
   * destroys the real name and its extension, which cannot be recovered.
   */
  originalFilename: string;
  /**
   * Optional human-facing label (`Authority To Release Assets Form`). Stored as
   * the file's display name; a backend processor may replace it with a richer
   * one derived from the underlying record.
   */
  displayName?: string;
}

/**
 * Update file metadata request
 */
export interface UpdateFileRequest {
  displayName?: string;
  fileType?: FileType;
  isCertifiedCopy?: boolean;
  /**
   * The user's own one-line note about the document.
   *
   * Send an empty string to clear it — the backend nulls the column, which
   * makes the row fall back to the document analysis's own sentence again. That
   * is the revert, and it is why clearing is not the same as sending nothing.
   */
  notes?: string;
}

/**
 * File filters for listing
 */
export interface FileFilters {
  fileType?: FileType;
  status?: FileStatus;
  uploadedBefore?: string;
  uploadedAfter?: string;
  search?: string;
}

/**
 * Files list response
 */
export interface FileListResponse {
  files: MatterFile[];
  total: number;
}

// Validation Schemas

export const FileTypeSchema = z.enum([
  'death-certificate',
  'will',
  'codicil',
  'grant-of-representation',
  'primary-id',
  'secondary-id',
  'correspondence-attachment',
  'institution-form',
  'institution-form-template',
  'institution-form-signed',
  'letterhead',
  'handwritten-signature',
  'email-signature',
  'additional-document',
  'distribution-declaration',
  'matter-document',
  'unknown-pending-ai-analysis'
]);

export const FileStatusSchema = z.enum([
  'uploaded',
  'processing',
  'available',
  'failed'
]);

export const GetPresignedUrlRequestSchema = z.object({
  fileType: FileTypeSchema,
  matterId: z.number().int().positive().optional(),
  isCertifiedCopy: z.boolean().optional(),
  lawFirmId: z.number().int().positive().optional(),
  lawFirmUserId: z.number().int().positive().optional(),
  contactId: z.number().int().positive().optional(),
  attachmentId: z.number().int().positive().optional(), // For correspondence attachments
  intakeFormId: z.string().optional(), // For linking uploads to an intake form
  analysisRequested: z.boolean().optional(),
  reclassify: z.boolean().optional(),
  metadata: z.record(z.string(), z.any()).optional()
});

export const UploadCompleteRequestSchema = z.object({
  fileId: z.number().int().positive(),
  originalFilename: z.string().min(1),
  displayName: z.string().min(1).max(255).optional()
});

export const FileFiltersSchema = z.object({
  fileType: FileTypeSchema.optional(),
  status: FileStatusSchema.optional(),
  uploadedBefore: z.string().datetime().optional(),
  uploadedAfter: z.string().datetime().optional(),
  search: z.string().optional()
});

// Action Result Types

export interface FileActionResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  validationErrors?: Record<string, string[]>;
}

// File Type Metadata

export interface FileTypeMetadata {
  label: string;
  description: string;
  /**
   * What the destination backend file type accepts, read from `lib/constants/file-type-accept`.
   *
   * Optional because one registry key is not an upload at all: the email signature is authored
   * in the app and stored as HTML on the user record, so it has no backend file type and states
   * no format contract. `validateFile` refuses an upload against such a key rather than guessing.
   */
  acceptedMimeTypes?: string[];
  maxSizeBytes: number;
  requiresCertification: boolean;
  icon?: string;
}

/**
 * Per-file-type display metadata.
 *
 * **`acceptedMimeTypes` is not authored here.** It is read from
 * `lib/constants/file-type-accept.ts`, which derives it from the backend's own
 * `FILE_TYPES[slug].allowedMimeTypes` (VALE-1150), so this registry cannot advertise a format
 * the upload would then be rejected for. Until VALE-209 these lists were typed by hand and had
 * drifted badly: GIF, WEBP, CSV, RTF and ZIP were offered on document types the backend
 * accepts none of, and `letterhead` offered JPG/PNG where the backend takes PDF only.
 *
 * The labels, sizes, icons and descriptions ARE this file's own — only the format contract
 * belongs to the backend.
 */
export const FILE_TYPE_METADATA: Record<FileType, FileTypeMetadata> = {
  'death-certificate': {
    label: 'Death Certificate',
    description: 'Official death certificate from vital records',
    acceptedMimeTypes: acceptedMimeTypesFor('death-certificate'),
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    requiresCertification: true,
    icon: 'FileText'
  },
  will: {
    label: 'Will',
    description: 'Last will and testament',
    acceptedMimeTypes: acceptedMimeTypesFor('will'),
    maxSizeBytes: 50 * 1024 * 1024, // 50MB
    requiresCertification: true,
    icon: 'ScrollText'
  },
  codicil: {
    label: 'Codicil',
    description: 'A document that amends the will',
    acceptedMimeTypes: acceptedMimeTypesFor('codicil'),
    maxSizeBytes: 50 * 1024 * 1024, // 50MB
    requiresCertification: true,
    icon: 'ScrollText'
  },
  'grant-of-representation': {
    label: 'Grant of Representation',
    description: 'Court-issued grant of representation',
    acceptedMimeTypes: acceptedMimeTypesFor('grant-of-representation'),
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    requiresCertification: true,
    icon: 'Scale'
  },
  'primary-id': {
    label: 'Primary ID',
    description: 'Government-issued primary identification',
    acceptedMimeTypes: acceptedMimeTypesFor('primary-id'),
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    requiresCertification: true,
    icon: 'IdCard'
  },
  'secondary-id': {
    label: 'Secondary ID',
    description: 'Secondary identification document',
    acceptedMimeTypes: acceptedMimeTypesFor('secondary-id'),
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    requiresCertification: false,
    icon: 'IdCard'
  },
  'correspondence-attachment': {
    label: 'Correspondence Attachment',
    description: 'File attached to correspondence',
    acceptedMimeTypes: acceptedMimeTypesFor('correspondence-attachment'),
    maxSizeBytes: 25 * 1024 * 1024, // 25MB
    requiresCertification: false,
    icon: 'Paperclip'
  },
  /**
   * A display-only label covering both institution-form types; it takes the signed type's
   * contract, the broader of the two.
   */
  'institution-form': {
    label: 'Institution Form',
    description: 'Form from financial institution',
    acceptedMimeTypes: acceptedMimeTypesFor('institution-form-signed'),
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    requiresCertification: false,
    icon: 'FileText'
  },
  'institution-form-template': {
    label: 'Institution Form Template',
    description: 'Template for institution forms',
    acceptedMimeTypes: acceptedMimeTypesFor('institution-form-template'),
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    requiresCertification: false,
    icon: 'FileText'
  },
  'institution-form-signed': {
    label: 'Signed Institution Form',
    description: 'Signed institution form',
    acceptedMimeTypes: acceptedMimeTypesFor('institution-form-signed'),
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    requiresCertification: false,
    icon: 'FileCheck'
  },
  letterhead: {
    label: 'Letterhead',
    description: 'Law firm letterhead',
    acceptedMimeTypes: acceptedMimeTypesFor('letterhead'),
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    requiresCertification: false,
    icon: 'FileText'
  },
  'handwritten-signature': {
    label: 'Handwritten Signature',
    description: 'Handwritten signature image',
    acceptedMimeTypes: acceptedMimeTypesFor('handwritten-signature'),
    maxSizeBytes: 5 * 1024 * 1024, // 5MB
    requiresCertification: false,
    icon: 'PenTool'
  },
  'email-signature': {
    label: 'Email Signature',
    description: 'Email signature template',
    /**
     * Not an uploaded file type, and so it states no format contract at all (VALE-1150). The
     * email signature is authored in the app and stored as HTML on the user record: there is no
     * backend `FileTypeSlug`, no upload, and nothing to validate. It had carried a local
     * `['text/html', 'text/plain']` literal, which described no door anyone could open. It stays
     * in this registry only for its label and icon.
     */
    maxSizeBytes: 5 * 1024 * 1024, // 5MB
    requiresCertification: false,
    icon: 'Mail'
  },
  'additional-document': {
    label: 'Additional Document',
    description: 'Supporting document for the matter',
    acceptedMimeTypes: acceptedMimeTypesFor('additional-document'),
    maxSizeBytes: 25 * 1024 * 1024, // 25MB
    requiresCertification: false,
    icon: 'FileText'
  },
  'distribution-declaration': {
    label: 'Distribution Declaration',
    description: "A beneficiary's distribution declaration document",
    acceptedMimeTypes: acceptedMimeTypesFor('distribution-declaration'),
    maxSizeBytes: 25 * 1024 * 1024, // 25MB
    requiresCertification: false,
    icon: 'file-signature'
  },
  /**
   * The document-type detector's fallback label (`lib/utils/file-type-detector.ts`) rather
   * than an upload type of its own — a file that lands here is stored as an
   * `additional-document`, which `DISPLAY_ONLY_ALIASES` records so the lookup resolves it.
   */
  'matter-document': {
    label: 'Matter Document',
    description: 'General document related to the matter',
    acceptedMimeTypes: acceptedMimeTypesFor('matter-document'),
    maxSizeBytes: 25 * 1024 * 1024, // 25MB
    requiresCertification: false,
    icon: 'File'
  },
  'unknown-pending-ai-analysis': {
    label: 'Document for AI Analysis',
    description: 'Document to be processed by AI for data extraction',
    acceptedMimeTypes: acceptedMimeTypesFor('unknown-pending-ai-analysis'),
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    requiresCertification: false,
    icon: 'Brain'
  }
};
